/**
 * Builds and writes an instruction document — the only write the browser makes
 * (host-instruction-protocol spec). The payload is shaped to exactly the fields
 * the Firestore rules allow (`INSTRUCTION_CREATE_FIELDS`): anything else would be
 * rejected server-side, and validating the params here first means the operator
 * sees the problem before the round trip. The instruction is created in the
 * database the target host reads.
 */
import { addDoc, collection, serverTimestamp, type Firestore } from 'firebase/firestore';
import {
    COLLECTION,
    isKindForHost,
    parseInstructionParams,
    type HostRole,
    type InstructionKind,
} from '@finvari/deployer';

export interface Actor {
    uid: string;
    email: string;
}

export interface CreateInstructionResult {
    ok: boolean;
    id?: string;
    error?: string;
}

/**
 * Drop keys whose value is `undefined`. A caller naturally writes an omitted
 * optional param as `{ reason: text || undefined }`, and zod's `.optional()`
 * keeps such a key in its output rather than deleting it — but `addDoc` rejects
 * the whole write on an `undefined` field value, so the instruction never
 * reaches the host. Shallow: every kind's params are flat scalars.
 */
function withoutUndefined(params: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined));
}

/**
 * Validate and construct the create payload (pure — no Firestore), so the same
 * shaping is testable and reusable by a dry-run/preview.
 */
export function buildInstructionPayload(
    host: HostRole,
    kind: InstructionKind,
    params: Record<string, unknown>,
    actor: Actor,
    release?: string,
): { ok: true; data: Record<string, unknown> } | { ok: false; error: string } {
    if (!isKindForHost(kind, host)) {
        return { ok: false, error: `${kind} is not an action the ${host} host runs` };
    }
    const parsed = parseInstructionParams(kind, params);
    if (!parsed.ok) return { ok: false, error: parsed.error ?? 'invalid parameters' };

    const data: Record<string, unknown> = {
        host,
        kind,
        params: withoutUndefined((parsed.value ?? {}) as Record<string, unknown>),
        createdBy: { uid: actor.uid, email: actor.email },
        createdAt: serverTimestamp(),
        status: 'pending',
    };
    if (release) data.release = release;
    return { ok: true, data };
}

/** Write the instruction to the correct database; returns the new doc id. */
export async function createInstruction(
    db: Firestore,
    host: HostRole,
    kind: InstructionKind,
    params: Record<string, unknown>,
    actor: Actor,
    release?: string,
): Promise<CreateInstructionResult> {
    const built = buildInstructionPayload(host, kind, params, actor, release);
    if (!built.ok) return { ok: false, error: built.error };
    try {
        const ref = await addDoc(collection(db, COLLECTION.instructions), built.data);
        return { ok: true, id: ref.id };
    } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
}
